/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as $ from "jquery";
import {UiToolbar} from "./UiToolbar";
import {UiComponentConfig} from "../generated/UiComponentConfig";
import {TeamAppsEvent} from "./util/TeamAppsEvent";
import {UiToolbarConfig} from "../generated/UiToolbarConfig";
import {UiTabConfig} from "../generated/UiTabConfig";
import {bind} from "./util/Bind";
import {UiItemViewConfig} from "../generated/UiItemViewConfig";
import {UiItemView} from "./UiItemView";
import {Emptyable, isEmptyable} from "./util/Emptyable";
import {UiToolButton} from "./micro-components/UiToolButton";
import {UiComponent} from "./UiComponent";
import {UiDropDown} from "./micro-components/UiDropDown";
import {TeamAppsUiContext} from "./TeamAppsUiContext";
import {executeWhenAttached} from "./util/ExecuteWhenAttached";
import {
	UiTabPanel_TabClosedEvent, UiTabPanel_TabNeedsRefreshEvent, UiTabPanel_TabSelectedEvent, UiTabPanel_ToolButtonClickedEvent, UiTabPanel_ToolButtonDropDownItemClickedEvent, UiTabPanelCommandHandler,
	UiTabPanelConfig, UiTabPanelEventSource
} from "../generated/UiTabPanelConfig";
import {UiToolButtonConfig} from "../generated/UiToolButtonConfig";
import {TeamAppsUiComponentRegistry} from "./TeamAppsUiComponentRegistry";
import {EventFactory} from "../generated/EventFactory";
import {UiTabPanelTabStyle} from "../generated/UiTabPanelTabStyle";

/**
 * @author Yann Massard (yamass@gmail.com)
 */
export interface TabConfiguration {
	tabId: string;
	icon: string;
	caption: string;
	closeable?: boolean;
	rightSide?: boolean;
	lazyLoading?: boolean;
}

interface Tab {
	config: TabConfiguration;
	$button: JQuery;
	$wrapper: JQuery;
	$dropDownTabButton: JQuery;
	$toolbarContainer: JQuery;
	$contentContainer: JQuery;
	toolbar: UiToolbar;
	contentComponent: UiComponent<UiComponentConfig>;
	buttonWidth?: number;
}

export class UiTabPanel extends UiComponent<UiTabPanelConfig> implements UiTabPanelCommandHandler, UiTabPanelEventSource, Emptyable {

	public readonly onTabSelected: TeamAppsEvent<UiTabPanel_TabSelectedEvent> = new TeamAppsEvent<UiTabPanel_TabSelectedEvent>(this);
	public readonly onTabNeedsRefresh: TeamAppsEvent<UiTabPanel_TabNeedsRefreshEvent> = new TeamAppsEvent<UiTabPanel_TabNeedsRefreshEvent>(this);
	public readonly onTabClosed: TeamAppsEvent<UiTabPanel_TabClosedEvent> = new TeamAppsEvent<UiTabPanel_TabClosedEvent>(this);
	public readonly onToolButtonClicked: TeamAppsEvent<UiTabPanel_ToolButtonClickedEvent> = new TeamAppsEvent<UiTabPanel_ToolButtonClickedEvent>(this);
	public readonly onToolButtonDropDownItemClicked: TeamAppsEvent<UiTabPanel_ToolButtonDropDownItemClickedEvent> = new TeamAppsEvent<UiTabPanel_ToolButtonDropDownItemClickedEvent>(this);

	public readonly onEmptyStateChanged: TeamAppsEvent<boolean> = new TeamAppsEvent(this);

	private $tabPanel: JQuery;
	private $leftButtonsWrapper: JQuery;
	private $rightButtonsWrapper: JQuery;
	private $dropDownButton: JQuery;
	private $dropDown: JQuery;
	private $dropButtonContainerLeft: JQuery;
	private $dropButtonContainerRight: JQuery;
	private $toolButtonContainer: JQuery;
	private $contentWrapper: JQuery;
	private $tabBar: JQuery;
	private $tabsContainer: JQuery;

	private leftTabs: Tab[] = [];
	private rightTabs: Tab[] = [];
	private selectedTab: Tab;
	private toolButtons: { [id: string]: UiToolButton };
	private toolButtonDropDown: UiDropDown;
	private hideTabBarIfSingleTab: boolean;

	constructor(config: UiTabPanelConfig, context: TeamAppsUiContext) {
		super(config, context);

		this.$tabPanel = $(`<div id="${config.id}" class="UiTabPanel">
    <div class="tab-panel-header teamapps-blurredBackgroundImage">
        <div class="background-color-div">
            <div class="tab-button-container left"></div>
            <div class="dropdown-button" tabindex="0"></div>
            <div class="spacer"></div>
            <div class="tab-button-container right"></div>
            <div class="tool-button-container tab-button"></div>
            <div class="tab-panel-dropdown hidden teamapps-blurredBackgroundImage">
                <div class="dropdown-button-container left"></div>
                <div class="dropdown-button-container right"></div>
            </div>
        </div>
    </div>
    <div class="tabpanel-content-wrapper-wrapper">
        <div class="tabpanel-content-wrapper"></div>
    </div>
</div>`);
		this.$tabBar = this.$tabPanel.find('>.tab-panel-header');
		this.$tabsContainer = this.$tabBar.find('>.background-color-div');
		this.$leftButtonsWrapper = this.$tabsContainer.find('>.tab-button-container.left');
		this.$rightButtonsWrapper = this.$tabsContainer.find('>.tab-button-container.right');
		this.$toolButtonContainer = this.$tabsContainer.find('>.tool-button-container');
		this.$dropDownButton = this.$tabsContainer.find('>.dropdown-button');
		this.$dropDown = this.$tabsContainer.find('>.tab-panel-dropdown');
		this.$dropButtonContainerLeft = this.$dropDown.find('>.dropdown-button-container.left');
		this.$dropButtonContainerRight = this.$dropDown.find('>.dropdown-button-container.right');
		this.$contentWrapper = this.$tabPanel.find('.tabpanel-content-wrapper');

		this.setHideTabBarIfSingleTab(!!config.hideTabBarIfSingleTab);
		this.setTabStyle(config.tabStyle);

		for (let i = 0; config.tabs && i < config.tabs.length; i++) {
			this.addTab(config.tabs[i], false, false);
		}
		if (config.selectedTabId) {
			this.selectTab(config.selectedTabId, false);
		} else {
			this.selectFirstVisibleTab();
		}

		this.$dropDownButton.click(() => {
			if (this.$dropDown.is(':visible')) {
				this.$dropDown.addClass("hidden");
			} else {
				this.$dropDown.removeClass("hidden");
				this.$dropDown.position({
					my: "right top",
					at: "right bottom",
					of: this.$dropDownButton
				});
			}
		}).blur(() => {
			this.$dropDown.addClass("hidden");
		});

		this.setToolButtons(config.toolButtons);
		this.toolButtonDropDown = new UiDropDown();
		this.toolButtonDropDown.onComponentRemoved = (component: UiComponent<UiComponentConfig>) => component.destroy();
		this.toolButtonDropDown.onClose = (component?: UiComponent<UiComponentConfig>) => component && component.destroy();
	}

	public setHideTabBarIfSingleTab(hideTabBarIfSingleTab: boolean) {
		this.hideTabBarIfSingleTab = hideTabBarIfSingleTab;
		this.updateTabBarVisibility();
	}

	private getAllTabs(): Tab[] {
		return this.leftTabs.concat(this.rightTabs);
	}

	public getMainDomElement(): JQuery {
		return this.$tabPanel;
	}

	private _createTab(tabConfig: TabConfiguration, toolbar: UiToolbar): Tab {
		const $tabButton = this.createTabButton(tabConfig.tabId, tabConfig.icon, tabConfig.caption, tabConfig.closeable);
		const $dropDownTabButton = this.createTabButton(tabConfig.tabId, tabConfig.icon, tabConfig.caption, tabConfig.closeable);

		if (tabConfig.rightSide) {
			this.$rightButtonsWrapper.append($tabButton);
			this.$dropButtonContainerRight.append($dropDownTabButton);
		} else {
			this.$leftButtonsWrapper.append($tabButton);
			this.$dropButtonContainerLeft.append($dropDownTabButton);
		}

		$tabButton.add($dropDownTabButton).mousedown(() => {
			this.selectTab(tabConfig.tabId, true);
		});

		const $tabContent = $(`<div class="tab-content-wrapper" data-tab-name="${tabConfig.tabId}">
                        <div class="tab-toolbar-container"/>
                        <div class="tab-component-container"/>
                    </div>`)
			.appendTo(this.$contentWrapper);

		let tab: Tab = {
			config: tabConfig,
			$button: $tabButton,
			$dropDownTabButton: $dropDownTabButton,
			$wrapper: $tabContent,
			$toolbarContainer: $tabContent.find(".tab-toolbar-container"),
			$contentContainer: $tabContent.find(".tab-component-container"),
			toolbar: null,
			contentComponent: null
		};

		if (toolbar) {
			this.setTabToolbarInternal(tab, toolbar);
		}

		return tab;
	}

	private createTabButton(tabId: string, iconName: string, caption: string, closeable: boolean) {
		const $tabButton = $(`<div class="tab-button" data-tab-name="${tabId}" draggable="true">
                     ${iconName ? `<div class="tab-button-icon"><div class="img img-16" style="background-image: url(${this._context.getIconPath(iconName, 16)});"/></div>` : ''}
                     <div class="tab-button-caption">${caption}</div>
                     <div class="tab-button-filler"/>
                </div>`);

		if (closeable) {
			const closeIconPath = "/static/resources/window-close-grey.png";
			let closeButtonHtml = `<div class="tab-button-close-button">
                        <div class="img ${this._context.config.optimizedForTouch ? 'img-16' : 'img-12'}" style="background-image: url(${closeIconPath});"/>
                    </div>`;
			const $closeButton1 = $(closeButtonHtml).appendTo($tabButton);
			$closeButton1.mousedown(() => {
				this.removeTab(tabId);
				this.onTabClosed.fire(EventFactory.createUiTabPanel_TabClosedEvent(this._config.id, tabId));
			});
		}
		return $tabButton;
	}

	public selectTab(tabId: string, sendSelectionEvent: boolean) {
		let tab = this.getTabById(tabId);
		if (!tab) {
			this.logger.error("Cannot select non-existing tab " + this._config.id + "~" + tabId);
			return;
		}
		this.selectedTab = tab;
		this.$leftButtonsWrapper
			.add(this.$rightButtonsWrapper)
			.add(this.$dropButtonContainerLeft)
			.add(this.$dropButtonContainerRight)
			.find('>.tab-button').removeClass('selected');
		tab.$button.addClass('selected');
		tab.$dropDownTabButton.addClass('selected');

		this.$contentWrapper.find('>.tab-content-wrapper').removeClass('selected');
		tab.$wrapper.addClass('selected');
		if (sendSelectionEvent) {
			this.onTabSelected.fire(EventFactory.createUiTabPanel_TabSelectedEvent(this.getId(), tabId));
		}
		if (this.selectedTab.contentComponent == null) {
			this.onTabNeedsRefresh.fire(EventFactory.createUiTabPanel_TabNeedsRefreshEvent(this.getId(), tabId));
		}
		if (tab.toolbar) {
			if (!tab.toolbar.attachedToDom) {
				tab.toolbar.attachedToDom = this.attachedToDom;
			} else {
				tab.toolbar.reLayout();
			}
		}
		if (tab.contentComponent) {
			if (!tab.contentComponent.attachedToDom) {
				tab.contentComponent.attachedToDom = this.attachedToDom;
			} else {
				tab.contentComponent.reLayout();
			}
		}
	}

	public setTabContent(tabId: string, contentConfig: UiComponentConfig) {
		let component = contentConfig != null ? this._context.createComponent(contentConfig) : null;
		this.setTabContentInstance(tabId, component);
	}

	public setTabContentInstance(tabId: string, content: UiComponent<UiComponentConfig>) {
		const tab = this.getTabById(tabId);
		const $tabContentContainer = tab.$contentContainer;
		if (tab.contentComponent) {
			isEmptyable(tab.contentComponent) && tab.contentComponent.onEmptyStateChanged.removeListener(this.onChildEmptyStateChanged.bind(this));
			tab.contentComponent.destroy();
			$tabContentContainer.empty();
		}
		tab.contentComponent = content;
		if (tab.contentComponent) {
			isEmptyable(tab.contentComponent) && tab.contentComponent.onEmptyStateChanged.addListener(this.onChildEmptyStateChanged.bind(this));
			content.getMainDomElement().appendTo($tabContentContainer);
		}

		this.onChildEmptyStateChanged(this.tabIsDeFactoEmpty(tab), tab.contentComponent);

		// after (!!) the parent has been informed about the empty state change, we should think about setting attached (and resizing!!!)
		if (this.selectedTab && this.selectedTab.config.tabId === tabId) {
			if (tab.contentComponent) {
				tab.contentComponent.attachedToDom = this.attachedToDom;
			} else if (tab.config.lazyLoading) {
				this.onTabNeedsRefresh.fire(EventFactory.createUiTabPanel_TabNeedsRefreshEvent(this.getId(), tabId));
			}
		}

		this.updateEmptyState();
	}

	setTabConfiguration(tabId: string, icon: string, caption: string, closeable: boolean, rightSide: boolean): void {
		let tab = this.getTabById(tabId);
		tab.$button.empty().append(this.createTabButton(tabId, icon, caption, closeable).find(">*"));
		tab.$dropDownTabButton.empty().append(this.createTabButton(tabId, icon, caption, closeable).find(">*"));
		if (rightSide && !tab.config.rightSide) {
			this.$rightButtonsWrapper.append(tab.$button);
			this.$dropButtonContainerRight.append(tab.$dropDownTabButton);
		} else if (!rightSide && tab.config.rightSide) {
			this.$leftButtonsWrapper.append(tab.$button);
			this.$dropButtonContainerLeft.append(tab.$dropDownTabButton);
		}
		tab.config.tabId = tabId;
		tab.config.icon = icon;
		tab.config.caption = caption;
		tab.config.closeable = closeable;
		tab.config.rightSide = rightSide;
	}

	public addTab(tabConfig: UiTabConfig, select: boolean, sendSelectionEvent: boolean) {
		let content: UiComponent<UiComponentConfig> = tabConfig.content && this._context.createComponent(tabConfig.content) as UiToolbar;
		let toolbar: UiToolbar = tabConfig.toolbar && this._context.createComponent(tabConfig.toolbar) as UiToolbar;
		this.addTabInstance(content, tabConfig, toolbar, select, sendSelectionEvent);
	}

	public addTabInstance(component: UiComponent<UiComponentConfig>, tabConfiguration: TabConfiguration, toolbar?: UiToolbar, select?: boolean, sendSelectionEvent?: boolean) {
		this.removeTab(tabConfiguration.tabId);
		let tab = this._createTab(tabConfiguration, toolbar);
		if (tabConfiguration.rightSide) {
			this.rightTabs.push(tab);
		} else {
			this.leftTabs.push(tab);
		}
		this.setTabContentInstance(tabConfiguration.tabId, component);
		if (select || this.getAllTabs().length === 1) {
			this.selectTab(tabConfiguration.tabId, sendSelectionEvent);
		}
		let tabBarVisibilityChanged = this.updateTabBarVisibility();
		if (tabBarVisibilityChanged) {
			this.reLayout(true);
		} else {
			this.relayoutButtons();
		}
		this.updateEmptyState();
	}

	protected onAttachedToDom() {
		this.getAllTabs().forEach(tab => {
			if (tab.toolbar) tab.toolbar.attachedToDom = true;
			if (tab.contentComponent) tab.contentComponent.attachedToDom = true;
		});
		this.reLayout();
	}

	public setTabToolbar(tabId: string, toolbarConfig: UiToolbarConfig) {
		let tab = this.getTabById(tabId);
		if (tab) {
			this.setTabToolbarInternal(tab, <UiToolbar>this._context.createComponent(toolbarConfig));
		}
	}

	private setTabToolbarInternal(tab: Tab, toolbar: UiToolbar) {
		if (tab.toolbar != null) {
			tab.toolbar.destroy();
			tab.toolbar.getMainDomElement().remove(); // TODO this should be part of the destroy method...
			tab.toolbar = null;
		}
		if (toolbar) {
			tab.toolbar = toolbar;
			tab.$toolbarContainer.append(toolbar.getMainDomElement());
		}
		if (this.selectedTab && this.selectedTab.config.tabId === tab.config.tabId && tab.toolbar) {
			tab.toolbar.attachedToDom = this.attachedToDom;
		}
	}

	public removeTab(tabId: string, destroyContentComponent = true) {
		let tab = this.getTabById(tabId);

		if (!tab) return;

		tab.contentComponent != null && isEmptyable(tab.contentComponent) && tab.contentComponent.onEmptyStateChanged.removeListener(this.onChildEmptyStateChanged.bind(this));
		if (destroyContentComponent && tab.contentComponent) {
			tab.contentComponent && tab.contentComponent.destroy();
		}
		tab.$button.remove();
		tab.$dropDownTabButton.remove();
		tab.$wrapper.remove();

		this.leftTabs = this.leftTabs.filter(tab => tab.config.tabId !== tabId);
		this.rightTabs = this.rightTabs.filter(tab => tab.config.tabId !== tabId);

		if (tab === this.selectedTab) {
			this.selectFirstVisibleTab();
		}

		let tabBarVisibilityChanged = this.updateTabBarVisibility();
		if (tabBarVisibilityChanged) {
			this.reLayout(true);
		} else {
			this.relayoutButtons();
		}
		this.updateEmptyState();
	}

	private selectFirstVisibleTab() {
		if (this.getVisibleTabs().length > 0) {
			this.selectTab(this.getVisibleTabs()[0].config.tabId, !this._context.executingCommand);
		} else {
			this.selectedTab = null;
		}
	}

	@bind
	private onChildEmptyStateChanged(empty: boolean, childComponent?: UiComponent<UiComponentConfig>) {
		if (!this.selectedTab || this.tabIsDeFactoEmpty(this.selectedTab)) {
			this.selectFirstVisibleTab();
		}
		this.relayoutButtons();
		this.updateTabBarVisibility();
		this.updateEmptyState();
	}

	private updateEmptyState() {
		this.onEmptyStateChanged.fireIfChanged(this.empty);
	}

	private getTabById(tabId: string): Tab {
		return this.getAllTabs().filter(tab => tab.config.tabId === tabId)[0];
	}

	private updateTabBarVisibility(): boolean {
		let wasHidden = this.$tabBar.is('.hidden');
		let isHidden = this.hideTabBarIfSingleTab && this.getVisibleTabs().length <= 1;
		this.$tabBar.toggleClass("hidden", isHidden);
		this.$tabBar[0].offsetHeight; // trigger reflow!
		return wasHidden != isHidden;
	}

	get empty(): boolean {
		return this.getNumberOfVisibleTabButtons() === 0;
	}

	private getVisibleTabs(): Tab[] {
		return this.getAllTabs()
			.filter(t => !this.tabIsDeFactoEmpty(t));

	}

	public getNumberOfVisibleTabButtons() {
		return this.getVisibleTabs().length;
	}

	private tabIsDeFactoEmpty(tab: Tab) {
		if (tab.contentComponent == null) {
			return !tab.config.lazyLoading;
		} else {
			isEmptyable(tab.contentComponent) && tab.contentComponent.empty;
		}
	}

	public getSelectedTabId() {
		return this.selectedTab && this.selectedTab.config.tabId;
	}

	public setToolButtons(toolButtons: UiToolButtonConfig[]) {
		this.$toolButtonContainer.empty().toggleClass("hidden", !toolButtons || toolButtons.length === 0);
		this.toolButtons = {};
		toolButtons && toolButtons.forEach(buttonConfig => {
			let toolButton = new UiToolButton(buttonConfig, () => {
				this.onToolButtonClicked.fire(EventFactory.createUiTabPanel_ToolButtonClickedEvent(this._config.id, buttonConfig.id));
			}, this._context);
			toolButton.getMainDomElement().appendTo(this.$toolButtonContainer);
			this.toolButtons[buttonConfig.id] = toolButton;
		});
	}

	public setTabStyle(tabStyle: UiTabPanelTabStyle) {
		this.$tabPanel.removeClass('tab-style-ears tab-style-blocks')
			.addClass(tabStyle === UiTabPanelTabStyle.EARS ? 'tab-style-ears' : 'tab-style-blocks');
		this.reLayout(true);
	}

	showToolButtonDropDown(toolButtonId: string, itemViewConfig: UiItemViewConfig, dropDownWidth = 300): void {
		let toolButton = this.toolButtons[toolButtonId];
		if (!toolButton) {
			console.error(`ToolButton with id ${toolButtonId} does not exist on UiPanel ${this._config.id}!`);
		}
		let itemView = new UiItemView(itemViewConfig, this._context);
		itemView.onItemClicked.addListener(eventObject => {
			this.toolButtonDropDown.close();
			this.onToolButtonDropDownItemClicked.fire(EventFactory.createUiTabPanel_ToolButtonDropDownItemClickedEvent(this.getId(), toolButtonId, eventObject.itemId));
		});
		this.toolButtonDropDown.setContent(itemView);
		this.toolButtonDropDown.open(toolButton.getMainDomElement(), {
			width: dropDownWidth
		});
	}

	@executeWhenAttached(true)
	public onResize(): void {
		if (!this.attachedToDom || this.getMainDomElement()[0].offsetWidth <= 0) return;
		if (this.selectedTab) {
			this.selectedTab.toolbar && this.selectedTab.toolbar.reLayout();
			this.selectedTab.contentComponent && this.selectedTab.contentComponent.reLayout();
		}
		this.relayoutButtons();
	}

	@executeWhenAttached(true)
	private relayoutButtons() {
		if (this.$tabBar.is('.hidden')) {
			return;
		}
		let availableWidth = this.$tabsContainer.width() - this.$dropDownButton[0].offsetWidth - this.$toolButtonContainer[0].offsetWidth;
		let sumOfButtonWidths = 0;
		this.getAllTabs().forEach(tab => {
			let tabFilled = !this.tabIsDeFactoEmpty(tab);

			if (tabFilled) {
				if (!tab.buttonWidth) {
					tab.buttonWidth = tab.$button[0].offsetWidth
				}
				sumOfButtonWidths += tab.buttonWidth;

				if (sumOfButtonWidths < availableWidth) {
					tab.$button.removeClass("hidden");
					tab.$dropDownTabButton.addClass("hidden");
				} else {
					tab.$button.addClass("hidden");
					tab.$dropDownTabButton.removeClass("hidden");
				}
			} else {
				tab.$button.add(tab.$dropDownTabButton).toggleClass("hidden", true);
			}
		});
		this.$dropDownButton.toggleClass("hidden", sumOfButtonWidths <= availableWidth);
	}

	public destroy(): void {
		this.getAllTabs().forEach(tab => {
			tab.toolbar && tab.toolbar.destroy();
			tab.contentComponent && tab.contentComponent.destroy();
		});
		this.toolButtonDropDown.destroy();
	}
}

TeamAppsUiComponentRegistry.registerComponentClass("UiTabPanel", UiTabPanel);